/**
 * Aegix One HTTP client — `@tiaki/http-client` per ADR 113 Phase 3.
 *
 * Direct adoption: One has no legacy interceptor stack to retire.
 * Routing through the shared lib gives One the auth-refresh queue,
 * `x-refreshed-token` rotation, stale-perms refresh (ADR 100), and
 * the `onTokenRefreshChange` / `onTokenRefreshComplete` listener API
 * — for free.
 *
 * Vendor-org masquerading is OFF: One is parent/guardian-facing
 * per ADR 075. There is no school-on-behalf-of flow.
 *
 * Storage layer + refresh path match AIM/SMS. `oneStorage` (the
 * single `StorageAdapter` instance from `lib/one-auth.ts`) backs both
 * `<AuthProvider>` and this http client via `httpStorageFromAdapter`,
 * so there's one source of truth across the lib's reconcile, cross-tab
 * sync, the WS-close-on-logout path, the boot-time refresh path, and
 * every request's `Authorization` header. The adapter's `legacyKeys`
 * capability handles the pre-canonical `aegix_one_*` slots AND the
 * `__cookie__` poison value declaratively — no inline migration shim.
 *
 * `refresh` is real: the backend adds `meta.refresh_token` to every
 * allauth response (via `JWTTokenStrategy.create_refresh_token`); the
 * `createSimpleJwtRefreshAdapter` invocation here posts that JWT to
 * `/api/v2/api-token-refresh/` to mint a new access, so the lib's
 * auth-refresh queue retries a 401 instead of short-circuiting to
 * `onAuthFailure`.
 */

import { createClient, type RefreshResult } from '@tiaki/http-client';
import type { paths } from '@tiaki/http-client/types';
import { httpStorageFromAdapter } from '@aegix/auth-core';
import i18n from '@/i18n/config';
import { oneStorage, oneRefreshAdapter } from './one-auth';
import { deviceIdBeforeRequest } from './deviceIdHeader';

export const client = createClient<paths>({
  baseURL: window.__ENV?.NX_API_URL ?? '',
  // Route storage through the SAME `oneStorage` adapter every other
  // ONE consumer reads from (provider, allauth-endpoints, sockets).
  // `httpStorageFromAdapter` builds the `{ getAccessToken,
  // setAccessToken, getRefreshToken, clearAuthSession }` shape
  // `createClient` expects — no raw localStorage/sessionStorage access
  // here. The adapter's `legacyKeys` capability promotes pre-canonical
  // `aegix_one_*` entries on first read; the `__cookie__` scrub and
  // JSON-quoted self-heal also live in the adapter.
  storage: httpStorageFromAdapter(oneStorage),
  // Real refresh path. The backend issues `meta.refresh_token`;
  // `oneRefreshAdapter` posts it to `/api/v2/api-token-refresh/` (the
  // canonical Tiaki endpoint AIM and SMS use) to mint a fresh access
  // token.
  refresh: async (refreshToken: string): Promise<RefreshResult> => {
    if (!refreshToken) return { token: null };
    try {
      const { jwt } = await oneRefreshAdapter.refresh(refreshToken);
      return { token: jwt };
    } catch {
      // Refresh failed — surface as 401 short-circuit so the lib
      // falls through to `onAuthFailure` and bounces to /login.
      return { token: null };
    }
  },
  onAuthFailure: () => {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  },
});

// Dynamically inject Accept-Language matching user's active locale preference
client.use({
  onRequest({ request }) {
    const lang = i18n.language || 'en';
    request.headers.set('Accept-Language', lang.split('-')[0]);
    return request;
  },
});

// Stamp the persisted push device id (`push/deviceId.ts`)
// on the raw-ky escape hatch (`client.ky.*` — visitor-management,
// reunification schema-gap calls, geofence pre-checkins, FCM
// register/deregister) so device-scoped session enforcement
// sees ONE traffic. The FCM calls set the header explicitly per call and
// the hook lets an explicit header win. The typed openapi-fetch surface
// is left alone ON PURPOSE — parity with AIM/SMS, whose `deviceIdHeader.ts`
// files document the fcm-token admin-sweep hazard a stamped typed surface
// invites; see `deviceIdHeader.ts` for the full scope note. The JWT-mint
// surfaces (allauth adapter + side-flow endpoints, raw fetch) are covered
// by `deviceIdFetch` in `lib/one-auth.ts` and `lib/allauth-endpoints.ts`.
client.ky = client.ky.extend({
  hooks: { beforeRequest: [deviceIdBeforeRequest] },
});

/**
 * Unwrap openapi-fetch's `{data, error}` shape into a `Promise<T>` that
 * throws on error — preserving the throwing semantics the old
 * `apiClient.get/post/patch` had so call sites need only swap the import.
 *
 * `ApiValidationError` (400 with field errors) already throws via the
 * lib's `surfaceValidationErrors` middleware; this branch handles other
 * non-2xx (e.g., 401 redirected, 404, 5xx).
 */
export function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined && result.error !== null) {
    const detail = (result.error as { detail?: string })?.detail;
    throw new Error(detail || 'Request failed');
  }
  return result.data as T;
}
